import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, FileWarning, MoreHorizontal, Trash2, Send, Clock } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

interface PostMortem {
  id: string
  title: string
  incidentDate: string
  severity: string
  status: string
  summary: string | null
  rootCause: string | null
  createdAt: string
}

function severityBadge(severity: string) {
  switch (severity) {
    case 'critical': return <Badge variant="destructive">Critical</Badge>
    case 'major': return <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20">Major</Badge>
    default: return <Badge variant="secondary">Minor</Badge>
  }
}

function statusBadge(status: string) {
  switch (status) {
    case 'published': return <Badge>Published</Badge>
    case 'review': return <Badge variant="outline">In Review</Badge>
    default: return <Badge variant="secondary">Draft</Badge>
  }
}

function CreatePostMortemDialog() {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [severity, setSeverity] = useState('major')
  const [summary, setSummary] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/post-mortems', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['post-mortems'] })
      toast({ title: 'Post-mortem created' })
      setOpen(false)
      setTitle(''); setSummary('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create post-mortem', variant: 'destructive' }),
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" /> New Post-Mortem</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Create Post-Mortem</DialogTitle><DialogDescription>Document an incident for review and learning.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Exchange Online outage - Client X" /></div>
          <div className="space-y-2">
            <Label>Severity</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="minor">Minor</SelectItem>
                <SelectItem value="major">Major</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Summary</Label>
            <textarea className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Brief summary of the incident..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => createMutation.mutate({ title, severity, summary: summary || undefined, incidentDate: new Date().toISOString() })} disabled={!title || createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function PostMortemPage() {
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['post-mortems'],
    queryFn: () => apiClient.get<{ postMortems: PostMortem[]; pagination: unknown }>('/post-mortems'),
  })

  const publishMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/post-mortems/${id}/publish`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['post-mortems'] })
      toast({ title: 'Published', description: 'Post-mortem published' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/post-mortems/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['post-mortems'] })
      toast({ title: 'Deleted' })
    },
  })

  const postMortems = data?.data?.postMortems || []
  const filtered = postMortems.filter((pm) => pm.title.toLowerCase().includes(search.toLowerCase()))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Post-Mortems</h1>
          <p className="text-muted-foreground">Incident analysis, root cause, and lessons learned</p>
        </div>
        <CreatePostMortemDialog />
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search post-mortems..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center py-12"><FileWarning className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" /><p className="text-lg font-medium text-muted-foreground">No post-mortems yet</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((pm) => (
            <Card key={pm.id}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{pm.title}</span>
                    {statusBadge(pm.status)}
                    {severityBadge(pm.severity)}
                  </div>
                  {pm.summary && <p className="text-sm text-muted-foreground line-clamp-1">{pm.summary}</p>}
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Incident: {new Date(pm.incidentDate).toLocaleDateString()}</span>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {pm.status !== 'published' && (
                      <DropdownMenuItem onClick={() => publishMutation.mutate(pm.id)}><Send className="mr-2 h-4 w-4" /> Publish</DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive" onClick={() => deleteMutation.mutate(pm.id)}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
